import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFactures, getFactureById, createFacture, updateFacture, deleteFacture,
  emettreFacture, createAvoir, dupliquerFacture, refreshRetards, downloadFacturX, downloadFacturePDFSimple,
  getClients, getArticles, getDevis, getDevisById,
  getEntreprise, viewFacturePDF,
  getPaiements, addPaiement, deletePaiement,
  envoyerFactureEmail, envoyerRappelEmail,
} from '../api';
import StatusBadge from './StatusBadge';
import SearchSelect from './SearchSelect';
import ConfirmModal from './ConfirmModal';

const STATUTS_PAIEMENT = [
  { value: 'impayee', label: 'Impayée' },
  { value: 'partiellement_payee', label: 'Partiel' },
  { value: 'payee', label: 'Payée' },
  { value: 'en_retard', label: 'En retard' },
];

const MODES_PAIEMENT = [
  { value: 'virement', label: 'Virement bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'especes', label: 'Espèces' },
  { value: 'carte', label: 'Carte bancaire' },
  { value: 'prelevement', label: 'Prélèvement' },
  { value: 'lcr', label: 'LCR / Billet à ordre' },
];

const TAUX_TVA_OPTIONS = [
  { value: '0', label: '0 % — Exonéré' },
  { value: '2.1', label: '2,1 % — Super-réduit' },
  { value: '5.5', label: '5,5 % — Réduit' },
  { value: '10', label: '10 % — Intermédiaire' },
  { value: '20', label: '20 % — Normal' },
];

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = () => ({
  client_id: '',
  devis_id: '',
  date_creation: today(),
  date_echeance: '',
  date_livraison: '',
  conditions_paiement: '',
  reference_commande: '',
  service_public: false,
  notes: '',
  lignes: [{ description: '', quantite: 1, prix_unitaire: 0, article_id: '', tva: 20, position: 0 }],
});

function Factures() {
  const [factures, setFactures] = useState([]);
  const [clients, setClients] = useState([]);
  const [articles, setArticles] = useState([]);
  const [devis, setDevis] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM());
  const [alert, setAlert] = useState(null);
  const [working, setWorking] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  // Modal email
  const [emailModal, setEmailModal] = useState(null); // { facture, isRappel }
  const [emailForm, setEmailForm] = useState({ email: '', message: '' });

  // Paiements
  const [paiementsModal, setPaiementsModal] = useState(null);
  const [paiementsData, setPaiementsData] = useState(null);
  const [paiementForm, setPaiementForm] = useState({ montant: '', date_paiement: today(), mode_paiement: 'virement', reference: '', notes: '' });
  const [paiementWorking, setPaiementWorking] = useState(false);
  const dragIndex = useRef(null);

  useEffect(() => {
    const close = () => setOpenDropdown(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [fRes, cRes, aRes, dRes] = await Promise.all([
        getFactures(), getClients(), getArticles(), getDevis(),
      ]);
      setFactures(fRes.data);
      setClients(cRes.data);
      setArticles(aRes.data);
      setDevis(dRes.data);
    } catch (err) {
      showAlert('Erreur lors du chargement', 'error');
    }
  }, []);

  useEffect(() => {
    refreshRetards().catch(() => {}).finally(() => loadAll());
  }, [loadAll]);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFacture(null);
    setFormData(EMPTY_FORM());
  };

  const openNew = async () => {
    const empty = EMPTY_FORM();
    try {
      const res = await getEntreprise();
      if (res.data?.conditions_paiement_defaut) {
        empty.conditions_paiement = res.data.conditions_paiement_defaut;
      }
    } catch (_) {}
    setEditingFacture(null);
    setFormData(empty);
    setShowModal(true);
  };

  const openEdit = async (facture) => {
    try {
      const res = await getFactureById(facture.id);
      const d = res.data;
      setEditingFacture(d);
      setFormData({
        client_id: String(d.client_id || ''),
        devis_id: d.devis_id ? String(d.devis_id) : '',
        date_creation: d.date_creation || today(),
        date_echeance: d.date_echeance || '',
        date_livraison: d.date_livraison || '',
        conditions_paiement: d.conditions_paiement || '',
        reference_commande: d.reference_commande || '',
        service_public: !!d.service_public,
        notes: d.notes || '',
        lignes: (d.lignes || []).map((l, idx) => ({
          description: l.description || l.article_nom || '',
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire,
          article_id: l.article_id ? String(l.article_id) : '',
          tva: l.tva != null ? l.tva : 20,
          total_ht: l.total_ht,
          position: l.position ?? idx,
        })),
      });
      setShowModal(true);
    } catch (_) {
      showAlert('Erreur lors du chargement de la facture', 'error');
    }
  };

  const handleDevisChange = async (devisId) => {
    if (!devisId) { setFormData({ ...formData, devis_id: '' }); return; }
    try {
      const res = await getDevisById(devisId);
      const d = res.data;
      setFormData((f) => ({
        ...f,
        devis_id: devisId,
        client_id: String(d.client_id || ''),
        notes: d.notes || '',
        lignes: (d.lignes || []).map((l, idx) => ({
          description: l.description || l.article_nom || '',
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire,
          article_id: l.article_id ? String(l.article_id) : '',
          tva: l.tva != null ? l.tva : (d.taux_tva || 20),
          total_ht: l.total_ht,
          position: l.position ?? idx,
        })),
      }));
    } catch (_) {}
  };

  const handleLigneChange = (index, field, value) => {
    const newLignes = [...formData.lignes];
    newLignes[index] = { ...newLignes[index], [field]: value };
    if (field === 'article_id' && value) {
      const art = articles.find(a => a.id === parseInt(value));
      if (art) {
        newLignes[index].prix_unitaire = art.prix_unitaire;
        const taux = art.taux_tva != null ? art.taux_tva : 20;
        newLignes[index].tva = taux;
        const q = parseFloat(newLignes[index].quantite) || 0;
        newLignes[index].total_ht = q * art.prix_unitaire;
        newLignes[index].total_ttc = newLignes[index].total_ht * (1 + taux / 100);
      }
    }
    if (field === 'quantite' || field === 'prix_unitaire') {
      const q = parseFloat(newLignes[index].quantite) || 0;
      const p = parseFloat(newLignes[index].prix_unitaire) || 0;
      newLignes[index].total_ht = q * p;
    }
    setFormData({ ...formData, lignes: newLignes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setWorking('save');
    try {
      const lignes = formData.lignes.map((l, idx) => ({
        ...l,
        quantite: parseFloat(l.quantite) || 0,
        prix_unitaire: parseFloat(l.prix_unitaire) || 0,
        tva: parseFloat(l.tva) != null && !isNaN(parseFloat(l.tva)) ? parseFloat(l.tva) : 20,
        total_ht: (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire) || 0),
        position: idx,
      }));
      const payload = {
        client_id: parseInt(formData.client_id),
        devis_id: formData.devis_id ? parseInt(formData.devis_id) : null,
        date_creation: formData.date_creation || today(),
        date_echeance: formData.date_echeance || null,
        date_livraison: formData.date_livraison || null,
        conditions_paiement: formData.conditions_paiement || null,
        reference_commande: formData.reference_commande || null,
        service_public: formData.service_public ? 1 : 0,
        notes: formData.notes || null,
        lignes,
      };
      if (editingFacture) {
        await updateFacture(editingFacture.id, payload);
        showAlert('Brouillon sauvegardé', 'success');
      } else {
        await createFacture(payload);
        showAlert('Facture créée en brouillon', 'success');
      }
      closeModal();
      loadAll();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setWorking(null);
    }
  };

  const handleEmettre = (f) => {
    setConfirm({
      title: 'Émettre la facture',
      message: `Un numéro séquentiel définitif sera attribué à cette facture.\n\nCette action est irréversible — la facture ne pourra plus être modifiée.`,
      confirmLabel: 'Émettre',
      variant: 'primary',
      onConfirm: async () => {
        setConfirm(null);
        setWorking(f.id + '_emettre');
        try {
          await emettreFacture(f.id);
          showAlert('Facture émise avec succès', 'success');
          loadAll();
        } catch (err) {
          showAlert(err.response?.data?.error || "Erreur lors de l'émission", 'error');
        } finally {
          setWorking(null);
        }
      },
    });
  };

  const handleDelete = (f) => {
    setConfirm({
      title: 'Supprimer le brouillon',
      message: 'Ce brouillon sera définitivement supprimé. Cette action est irréversible.',
      confirmLabel: 'Supprimer',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        setWorking(f.id + '_delete');
        try {
          await deleteFacture(f.id);
          showAlert('Brouillon supprimé', 'success');
          loadAll();
        } catch (err) {
          showAlert(err.response?.data?.error || 'Erreur lors de la suppression', 'error');
        } finally {
          setWorking(null);
        }
      },
    });
  };

  const handleDupliquer = async (f) => {
    setWorking(f.id + '_dupliquer');
    try {
      await dupliquerFacture(f.id);
      showAlert('Facture dupliquée — un brouillon a été créé', 'success');
      loadAll();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur lors de la duplication', 'error');
    } finally {
      setWorking(null);
    }
  };

  const handleAvoir = (f) => {
    setConfirm({
      title: `Créer un avoir sur ${f.numero}`,
      message: `Un brouillon d'avoir sera créé avec toutes les lignes en négatif.\n\nVous pourrez le modifier (avoir partiel) puis l'émettre.`,
      confirmLabel: "Créer l'avoir",
      variant: 'primary',
      onConfirm: async () => {
        setConfirm(null);
        setWorking(f.id + '_avoir');
        try {
          const res = await createAvoir(f.id);
          showAlert(`Avoir créé en brouillon (id ${res.data.id})`, 'success');
          loadAll();
        } catch (err) {
          showAlert(err.response?.data?.error || "Erreur lors de la création de l'avoir", 'error');
        } finally {
          setWorking(null);
        }
      },
    });
  };

  const handleStatutChange = async (f, newStatut) => {
    setWorking(f.id + '_statut');
    try {
      await updateFacture(f.id, { statut: newStatut });
      loadAll();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur lors de la mise à jour du statut', 'error');
    } finally {
      setWorking(null);
    }
  };

  const openEmailModal = (f, isRappel = false) => {
    setEmailModal({ facture: f, isRappel });
    setEmailForm({ email: f.client_email || '', message: '' });
  };

  const closeEmailModal = () => { setEmailModal(null); setEmailForm({ email: '', message: '' }); };

  const handleEnvoyerEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailModal) return;
    const { facture, isRappel } = emailModal;
    setWorking(`${facture.id}_email`);
    try {
      const fn = isRappel ? envoyerRappelEmail : envoyerFactureEmail;
      const res = await fn(facture.id, emailForm.email, emailForm.message);
      showAlert(res.data.message || (isRappel ? 'Rappel envoyé' : 'Facture envoyée'), 'success');
      closeEmailModal();
    } catch (err) {
      showAlert(err.response?.data?.error || "Erreur lors de l'envoi", 'error');
    } finally {
      setWorking(null);
    }
  };

  const handleMarquerPret = async (f) => {
    setWorking(`${f.id}_pret`);
    try {
      await updateFacture(f.id, { super_pdp_statut: 'pret_a_transmettre' });
      await loadAll();
      showAlert("Facture signalée — votre administrateur va s'en occuper", 'success');
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur', 'error');
    } finally {
      setWorking(null);
    }
  };

  const openPaiements = async (f) => {
    setPaiementsModal(f);
    setPaiementsData(null);
    setPaiementForm({ montant: '', date_paiement: today(), mode_paiement: 'virement', reference: '', notes: '' });
    try {
      const res = await getPaiements(f.id);
      setPaiementsData(res.data);
    } catch {
      showAlert('Erreur lors du chargement des paiements', 'error');
    }
  };

  const closePaiements = () => { setPaiementsModal(null); setPaiementsData(null); };

  const handleAddPaiement = async (e) => {
    e.preventDefault();
    setPaiementWorking(true);
    try {
      await addPaiement(paiementsModal.id, paiementForm);
      const res = await getPaiements(paiementsModal.id);
      setPaiementsData(res.data);
      setPaiementForm({ montant: '', date_paiement: today(), mode_paiement: 'virement', reference: '', notes: '' });
      loadAll();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur lors de l\'enregistrement', 'error');
    } finally {
      setPaiementWorking(false);
    }
  };

  const handleDeletePaiement = (p) => {
    setConfirm({
      title: 'Supprimer ce paiement',
      message: `Supprimer le paiement de ${Number(p.montant).toFixed(2)} € du ${new Date(p.date_paiement + 'T00:00:00').toLocaleDateString('fr-FR')} ?`,
      confirmLabel: 'Supprimer',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await deletePaiement(p.id);
          const res = await getPaiements(paiementsModal.id);
          setPaiementsData(res.data);
          loadAll();
        } catch (err) {
          showAlert(err.response?.data?.error || 'Erreur lors de la suppression', 'error');
        }
      },
    });
  };

  const displayNumero = (f) =>
    f.numero && f.numero.startsWith('BROUILLON-') ? '—' : f.numero;

  const isBusy = (id, action) => working === `${id}_${action}`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>Factures</h2>
        <button className="btn btn-primary" onClick={openNew}>+ Nouvelle facture</button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>
          {alert.message}
        </div>
      )}

      {(() => {
        const signaled = factures.filter(f => f.super_pdp_statut === 'pret_a_transmettre').length;
        return signaled > 0 ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', marginBottom: 16,
            background: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)',
            borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--color-info)',
          }}>
            <span style={{ fontWeight: 600 }}>
              {signaled} facture{signaled > 1 ? 's' : ''} signalée{signaled > 1 ? 's' : ''} à votre administrateur pour transmission au registre fiscal
            </span>
            <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>
              {' '}— votre administrateur s'en occupe.
            </span>
          </div>
        ) : null;
      })()}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Numéro</th>
              <th>Client</th>
              <th>Date</th>
              <th>Total TTC</th>
              <th>Statut</th>
              <th>E-invoicing</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {factures.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center' }}>Aucune facture</td></tr>
            ) : factures.map(f => (
              <tr key={f.id}>
                <td className="num">
                  {f.type_document === 381 && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-primary)', marginRight: 4, textTransform: 'uppercase' }}>Avoir</span>
                  )}
                  {displayNumero(f)}
                </td>
                <td>{f.client_nom}</td>
                <td>{new Date(f.date_creation + 'T00:00:00').toLocaleDateString('fr-FR')}</td>
                <td className="amount">{Math.abs(Number(f.total_ttc || 0)).toFixed(2)} €{f.type_document === 381 ? ' (crédit)' : ''}</td>
                <td><StatusBadge statut={f.statut} /></td>
                <td>
                  {f.statut !== 'brouillon' && (
                    <StatusBadge statut={f.super_pdp_statut || 'non_transmise'} />
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (openDropdown === f.id) { setOpenDropdown(null); return; }
                      const rect = e.currentTarget.getBoundingClientRect();
                      setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                      setOpenDropdown(f.id);
                    }}
                  >
                    Actions ▾
                  </button>
                  {openDropdown === f.id && (
                    <div className="dropdown-menu" style={{ top: dropdownPos.top, right: dropdownPos.right }} onClick={(e) => e.stopPropagation()}>
                      {f.statut === 'brouillon' ? (
                        <>
                          <button className="dropdown-item" onClick={() => { openEdit(f); setOpenDropdown(null); }}>
                            Modifier le brouillon
                          </button>
                          <div className="dropdown-divider" />
                          <button className="dropdown-item" onClick={() => { viewFacturePDF(f.id); setOpenDropdown(null); }}>
                            Aperçu PDF
                          </button>
                          <div className="dropdown-divider" />
                          <button className="dropdown-item" onClick={() => { handleEmettre(f); setOpenDropdown(null); }} disabled={!!working}>
                            Émettre la facture
                          </button>
                          <div className="dropdown-divider" />
                          <button className="dropdown-item" onClick={() => { handleDupliquer(f); setOpenDropdown(null); }} disabled={!!working}>
                            Dupliquer en brouillon
                          </button>
                          <div className="dropdown-divider" />
                          <button className="dropdown-item dropdown-item-danger" onClick={() => { handleDelete(f); setOpenDropdown(null); }} disabled={!!working}>
                            Supprimer le brouillon
                          </button>
                        </>
                      ) : (
                        <>
                          {!['deposee', 'acceptee'].includes(f.super_pdp_statut) && (
                            <>
                              <button className="dropdown-item" onClick={() => { openEdit(f); setOpenDropdown(null); }}>
                                Modifier la facture
                              </button>
                              <div className="dropdown-divider" />
                            </>
                          )}
                          <button className="dropdown-item" onClick={() => { viewFacturePDF(f.id); setOpenDropdown(null); }}>
                            Voir le PDF
                          </button>
                          <button className="dropdown-item" onClick={() => { downloadFacturX(f.id); setOpenDropdown(null); }}>
                            Télécharger le XML Factur-X
                          </button>
                          <button className="dropdown-item" onClick={() => { downloadFacturePDFSimple(f.id); setOpenDropdown(null); }}>
                            Télécharger le PDF simple (sans XML)
                          </button>
                          <button className="dropdown-item" onClick={() => { handleDupliquer(f); setOpenDropdown(null); }} disabled={!!working}>
                            Dupliquer en brouillon
                          </button>
                          <div className="dropdown-divider" />
                          <button
                            className="dropdown-item"
                            onClick={() => { openEmailModal(f, false); setOpenDropdown(null); }}
                          >
                            Envoyer par email
                          </button>
                          {['impayee', 'partiellement_payee', 'en_retard'].includes(f.statut) && (
                            <button
                              className="dropdown-item"
                              onClick={() => { openEmailModal(f, true); setOpenDropdown(null); }}
                            >
                              Envoyer un rappel
                            </button>
                          )}
                          {f.type_document !== 381 && (
                            <>
                              <div className="dropdown-divider" />
                              <button className="dropdown-item" onClick={() => { openPaiements(f); setOpenDropdown(null); }}>
                                Enregistrer un paiement
                              </button>
                              <div className="dropdown-divider" />
                              <button className="dropdown-item" onClick={() => { handleAvoir(f); setOpenDropdown(null); }} disabled={!!working}>
                                Créer un avoir
                              </button>
                            </>
                          )}
                          {(f.super_pdp_statut === 'non_transmise' || !f.super_pdp_statut) && (
                            <>
                              <div className="dropdown-divider" />
                              <div className="dropdown-label">E-reporting</div>
                              <button
                                className="dropdown-item"
                                onClick={() => { handleMarquerPret(f); setOpenDropdown(null); }}
                                disabled={!!working}
                              >
                                Signaler prête à transmettre
                              </button>
                            </>
                          )}
                          <div className="dropdown-divider" />
                          <div className="dropdown-label">Changer le statut</div>
                          {STATUTS_PAIEMENT.filter(s => s.value !== f.statut).map(s => (
                            <button key={s.value} className="dropdown-item" onClick={() => { handleStatutChange(f, s.value); setOpenDropdown(null); }}>
                              Marquer : {s.label}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 860 }}>
            <div className="modal-header">
              <h2>{editingFacture ? (editingFacture.statut === 'brouillon' ? 'Modifier le brouillon' : 'Modifier la facture') : 'Nouvelle facture'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {!editingFacture && (
                <div className="form-group">
                  <label>Créer depuis un devis (optionnel)</label>
                  <SearchSelect
                    placeholder="Aucun devis — rechercher par numéro ou client…"
                    options={devis.map(d => ({ value: d.id, label: `${d.numero} — ${d.client_nom}` }))}
                    value={formData.devis_id}
                    onChange={handleDevisChange}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Client *</label>
                <SearchSelect
                  required
                  placeholder="Rechercher un client…"
                  options={clients.map(c => ({ value: c.id, label: c.nom }))}
                  value={formData.client_id}
                  onChange={(val) => {
                    // Pré-remplit le drapeau service public depuis le type d'acheteur du client
                    const client = clients.find((c) => String(c.id) === String(val));
                    setFormData({ ...formData, client_id: val, service_public: client?.cadre_facturation === 'A1' });
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Date d'émission *</label>
                  <input type="date" required value={formData.date_creation}
                    onChange={(e) => setFormData({ ...formData, date_creation: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Date d'échéance</label>
                  <input type="date" value={formData.date_echeance}
                    onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Date de livraison / prestation</label>
                  <input type="date" value={formData.date_livraison}
                    onChange={(e) => setFormData({ ...formData, date_livraison: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Conditions de paiement</label>
                  <input type="text" value={formData.conditions_paiement}
                    placeholder="Ex : Paiement à 30 jours par virement bancaire"
                    onChange={(e) => setFormData({ ...formData, conditions_paiement: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Réf. commande acheteur</label>
                  <input type="text" value={formData.reference_commande}
                    placeholder="Numéro de bon de commande"
                    onChange={(e) => setFormData({ ...formData, reference_commande: e.target.value })} />
                </div>
              </div>

              <div className="form-group" style={{ padding: 15, backgroundColor: 'var(--color-bg)', borderRadius: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.service_public}
                    onChange={(e) => setFormData({ ...formData, service_public: e.target.checked })}
                    style={{ marginRight: 10, width: 18, height: 18 }}
                  />
                  <span style={{ fontWeight: 600 }}>Facture destinée à un service public (Chorus Pro)</span>
                </label>
                <p className="form-help">Cochée : cadre de facturation A1 dans le Factur-X. Non cochée : M1 (B2B). Pré-remplie selon le client.</p>
              </div>

              <div className="form-group">
                <label>Lignes</label>
                {formData.lignes.map((ligne, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={() => { dragIndex.current = index; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragIndex.current;
                      if (from === null || from === index) return;
                      const newLignes = [...formData.lignes];
                      const [moved] = newLignes.splice(from, 1);
                      newLignes.splice(index, 0, moved);
                      dragIndex.current = null;
                      setFormData({ ...formData, lignes: newLignes });
                    }}
                    style={{ border: '1px solid var(--border-color)', padding: 12, marginBottom: 8, borderRadius: 4 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ cursor: 'grab', color: 'var(--color-text-muted)', fontSize: 16, userSelect: 'none', lineHeight: 1 }} title="Glisser pour réordonner">⠿</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>Ligne {index + 1}</span>
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label>Article</label>
                      <SearchSelect
                        placeholder="Article du catalogue…"
                        options={articles.map(a => ({ value: a.id, label: `${a.nom} — ${a.prix_unitaire.toFixed(2)} €` }))}
                        value={ligne.article_id}
                        onChange={(val) => handleLigneChange(index, 'article_id', val)}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label>Description</label>
                      <textarea
                        rows={2}
                        value={ligne.description}
                        onChange={(e) => handleLigneChange(index, 'description', e.target.value)}
                        style={{ resize: 'vertical' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Qté</label>
                        <input type="number" step="0.01" value={ligne.quantite}
                          onChange={(e) => handleLigneChange(index, 'quantite', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Prix unit. (€)</label>
                        <input type="number" step="0.01" value={ligne.prix_unitaire}
                          onChange={(e) => handleLigneChange(index, 'prix_unitaire', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>TVA</label>
                        <select value={String(ligne.tva)}
                          onChange={(e) => handleLigneChange(index, 'tva', parseFloat(e.target.value))}>
                          {TAUX_TVA_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Total HT</label>
                        <input type="text" readOnly
                          value={`${((parseFloat(ligne.quantite) || 0) * (parseFloat(ligne.prix_unitaire) || 0)).toFixed(2)} €`} />
                      </div>
                      <button type="button" className="btn btn-danger btn-sm"
                        onClick={() => setFormData({ ...formData, lignes: formData.lignes.filter((_, i) => i !== index) })}>
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary"
                  onClick={() => setFormData({ ...formData, lignes: [...formData.lignes, { description: '', quantite: 1, prix_unitaire: 0, article_id: '', tva: 20, position: formData.lignes.length }] })}>
                  + Ajouter une ligne
                </button>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} rows={2}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ width: '100%', resize: 'vertical' }} />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={working === 'save'}>
                  {working === 'save' ? 'Sauvegarde…' : (editingFacture ? 'Sauvegarder' : 'Créer le brouillon')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal envoi email */}
      {emailModal && (
        <div className="modal" onClick={closeEmailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>{emailModal.isRappel ? 'Envoyer un rappel' : 'Envoyer la facture'} — {emailModal.facture.numero}</h2>
              <button className="close-btn" onClick={closeEmailModal}>×</button>
            </div>
            <form onSubmit={handleEnvoyerEmailSubmit}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Destinataire</label>
                  <input
                    className="form-input"
                    type="email"
                    required
                    value={emailForm.email}
                    onChange={(e) => setEmailForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@client.fr"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Message (optionnel)</label>
                  <textarea
                    className="form-input"
                    rows={5}
                    value={emailForm.message}
                    onChange={(e) => setEmailForm(f => ({ ...f, message: e.target.value }))}
                    placeholder={emailModal.isRappel
                      ? "Bonjour, je me permets de vous relancer concernant la facture ci-jointe…"
                      : "Bonjour, veuillez trouver ci-joint votre facture…"}
                    style={{ resize: 'vertical' }}
                  />
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-slate-400)' }}>
                    Ce message apparaîtra dans le corps de l'email, avant le PDF joint.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEmailModal}>Annuler</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={working === `${emailModal.facture.id}_email`}
                >
                  {working === `${emailModal.facture.id}_email` ? 'Envoi en cours…' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal paiements */}
      {paiementsModal && (
        <div className="modal" onClick={closePaiements}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h2>Encaissements — {paiementsModal.numero}</h2>
              <button className="close-btn" onClick={closePaiements}>×</button>
            </div>

            {paiementsData && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                  <div className="card" style={{ flex: 1, padding: '12px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Total TTC</div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{Number(paiementsData.facture.total_ttc).toFixed(2)} €</div>
                  </div>
                  <div className="card" style={{ flex: 1, padding: '12px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Encaissé</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-success)' }}>{Number(paiementsData.facture.total_paye).toFixed(2)} €</div>
                  </div>
                  <div className="card" style={{ flex: 1, padding: '12px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Reste à payer</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: paiementsData.facture.reste > 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                      {Number(paiementsData.facture.reste).toFixed(2)} €
                    </div>
                  </div>
                </div>

                {paiementsData.paiements.length > 0 && (
                  <table className="table" style={{ marginBottom: 16 }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Mode</th>
                        <th>Référence</th>
                        <th style={{ textAlign: 'right' }}>Montant</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paiementsData.paiements.map(p => (
                        <tr key={p.id}>
                          <td>{new Date(p.date_paiement + 'T00:00:00').toLocaleDateString('fr-FR')}</td>
                          <td>{MODES_PAIEMENT.find(m => m.value === p.mode_paiement)?.label || p.mode_paiement}</td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{p.reference || '—'}</td>
                          <td className="amount">{Number(p.montant).toFixed(2)} €</td>
                          <td>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeletePaiement(p)}>×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {paiementsData?.facture.reste > 0 && (
              <form onSubmit={handleAddPaiement}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
                  Ajouter un encaissement
                </h3>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Montant (€) *</label>
                    <input
                      type="number" step="0.01" min="0.01"
                      required
                      value={paiementForm.montant}
                      placeholder={`Max : ${Number(paiementsData.facture.reste).toFixed(2)}`}
                      onChange={(e) => setPaiementForm({ ...paiementForm, montant: e.target.value })}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Date *</label>
                    <input
                      type="date" required
                      value={paiementForm.date_paiement}
                      onChange={(e) => setPaiementForm({ ...paiementForm, date_paiement: e.target.value })}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Mode de paiement</label>
                    <select value={paiementForm.mode_paiement} onChange={(e) => setPaiementForm({ ...paiementForm, mode_paiement: e.target.value })}>
                      {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Référence</label>
                    <input type="text" value={paiementForm.reference} placeholder="N° chèque, virement…"
                      onChange={(e) => setPaiementForm({ ...paiementForm, reference: e.target.value })} />
                  </div>
                </div>
                <div className="actions">
                  <button type="submit" className="btn btn-primary" disabled={paiementWorking}>
                    {paiementWorking ? 'Enregistrement…' : 'Enregistrer le paiement'}
                  </button>
                </div>
              </form>
            )}

            {paiementsData?.facture.reste === 0 && (
              <p style={{ color: 'var(--color-success)', fontWeight: 600, textAlign: 'center', padding: '16px 0' }}>
                Facture entièrement payée
              </p>
            )}

            {!paiementsData && (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement…</p>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant || 'primary'}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

export default Factures;
